import { Component, Input, OnInit, ChangeDetectionStrategy, Output, EventEmitter, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { ContractsService } from '../../../../services/contracts.service';
import { 
  ContractField, 
  ProcessContractDataRequest, 
  ContractProcessingResult,
  FailedCompanyData,
  RetryFailedCompaniesRequest
} from '../../../../interfaces/contract-import.interface';
import { FailedCompaniesDialogComponent, FailedCompaniesDialogData } from '../../../../shared/components/failed-companies-dialog/failed-companies-dialog.component';

export interface ExcelData {
  filename: string;
  rows_count: number;
  columns: string[];
  data: any[];
}

export interface RowPreviewData {
  rowIndex: number;
  fields: { column: string; value: any; matchStatus?: { isMatched: boolean, matchInfo?: string, statusType?: string } | null }[];
}

export interface ContractListDialogData {
  excelData: ExcelData;
}

@Component({
  selector: 'app-contract-list-preview',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatExpansionModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDialogModule
  ],
  templateUrl: './contract-list-preview.component.html',
  styleUrl: './contract-list-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractListPreviewComponent implements OnInit {
  @Input() excelData: ExcelData | null = null;
  @Input() showPreview: boolean = false;
  @Input() isDialogMode: boolean = false;
  
  @Output() processData = new EventEmitter<ExcelData>();
  @Output() closeDialog = new EventEmitter<void>();

  previewRows: RowPreviewData[] = [];
  maxPreviewRows: number = 10; // Maximum number of rows to show in preview
  
  // Contract dropdown functionality
  contractFields: ContractField[] = [];
  fieldMappings: { [key: string]: string | null } = {}; // rowIndex-columnName -> contracts table field
  
  isProcessing = false;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: ContractListDialogData | null,
    @Optional() public dialogRef: MatDialogRef<ContractListPreviewComponent> | null,
    private contractsService: ContractsService,
    private dialog: MatDialog
  ) {
    // Ha dialog módban vagyunk, az adatokat a dialogData-ból vesszük
    if (this.dialogData) {
      this.excelData = this.dialogData.excelData;
      this.isDialogMode = true;
      this.showPreview = false;
    }
  }

  ngOnInit() {
    if (this.excelData && this.excelData.data) {
      this.preparePreviewData();
    }
    // Load contracts for dropdowns
    this.loadContracts();
  }

  private loadContracts() {
    this.contractsService.getContractFieldsForImport().subscribe({
      next: (response) => {
        this.contractFields = response.fields;
        // Automatikus mező párosítás beállítása
        this.setDefaultFieldMappings();
      },
      error: (error) => {
        console.error('Hiba a szerződés mezők betöltése során:', error);
      }
    });
  }

  private setDefaultFieldMappings() {
    if (!this.excelData || !this.excelData.data) return;

    // Minden sor és oszlop kombinációhoz ellenőrizzük az automatikus párosítást
    this.previewRows.forEach(row => {
      row.fields.forEach(field => {
        if (this.isContractField(field.column)) {
          const defaultField = this.getDefaultFieldForColumn(field.column);
          if (defaultField) {
            const key = this.getContractSelectionKey(row.rowIndex, field.column);
            this.fieldMappings[key] = defaultField;
          }
        }
      });
    });
  }

  private getDefaultFieldForColumn(columnName: string): string | null {
    const lowerColumn = columnName.toLowerCase();
    
    if (lowerColumn.includes('szerződés szám') || lowerColumn.includes('szerződésszám')) {
      return 'policynumber';
    }
    if (lowerColumn.includes('kock. vis. kezdete') || lowerColumn.includes('kockázat')) {
      return 'startdate';
    }
    if (lowerColumn.includes('díjjal fedezve') || lowerColumn.includes('fedezve')) {
      return 'settlementdate';
    }
    if (lowerColumn.includes('szerződés státusza') || lowerColumn.includes('státusz')) {
      return 'status';
    }
    if (lowerColumn.includes('dijfiz. gyak') || lowerColumn.includes('díjfizetési gyakoriság')) {
      return 'paymentfrequency';
    }
    if (lowerColumn.includes('szerződés díja') || lowerColumn.includes('díj összeg')) {
      return 'insurancepremium';
    }
    if (lowerColumn.includes('évesített díj')) {
      return 'annualpremium';
    }
    if (lowerColumn.includes('pénz nem') || lowerColumn.includes('pénznem')) {
      return 'currency';
    }
    if (lowerColumn.includes('lejárat')) {
      return 'enddate';
    }
    if (lowerColumn.includes('megszűnés dátuma') || lowerColumn.includes('megszűnés')) {
      return 'deletedate';
    }
    
    return null;
  }

  ngOnChanges() {
    if (this.excelData && this.excelData.data) {
      this.preparePreviewData();
    }
  }

  private preparePreviewData() {
    if (!this.excelData || !this.excelData.data) return;

    const rowsToShow = this.showPreview 
      ? Math.min(this.maxPreviewRows, this.excelData.data.length)
      : this.excelData.data.length;

    this.previewRows = this.excelData.data.slice(0, rowsToShow).map((row, index) => ({
      rowIndex: index + 1,
      fields: this.excelData!.columns.map(column => ({
        column: column,
        value: row[column] || '',
        matchStatus: this.getMatchStatus(row, column)
      }))
    }));
    
    // Ha vannak contract fields, akkor beállítjuk az alapértelmezett párosításokat
    if (this.contractFields.length > 0) {
      this.setDefaultFieldMappings();
    }
  }

  getFieldValue(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Igen' : 'Nem';
    return String(value);
  }

  getMatchStatus(row: any, column: string): { isMatched: boolean, matchInfo?: string, statusType?: string } | null {
    // Ha ez a Szerződő neve, Biztosított vagy Termék kód oszlop, keressük a match info-t
    if (column === 'Szerződő neve' || column === 'Biztosított' || 
        column.toLowerCase().includes('termék kód') || column.toLowerCase().includes('termékkód') ||
        column.toLowerCase().includes('product code') || column.toLowerCase().includes('productcode')) {
      const matchInfoKey = `${column}_match_info`;
      const matchInfo = row[matchInfoKey];
      
      if (matchInfo) {
        const isSuccessfulMatch = matchInfo.status === 'Sikeres párosítás';
        const isNewEntity = matchInfo.status === 'Új ügyfél létrehozása' || 
                           matchInfo.status === 'Új cég létrehozása' ||
                           matchInfo.status === 'Új termék létrehozása szükséges';
        let displayText = '';
        let statusType = '';
        
        if (isSuccessfulMatch) {
          // Termék esetén más formátum
          if (matchInfo.type === 'product') {
            displayText = `${matchInfo.status}: ${matchInfo.code} (ID: ${matchInfo.id})`;
          } else {
            displayText = `${matchInfo.status}: ${matchInfo.name} (ID: ${matchInfo.id})`;
          }
          statusType = 'success';
        } else if (matchInfo.status === 'Új termék létrehozása szükséges') {
          displayText = matchInfo.status;
          statusType = 'new-product';
        } else if (matchInfo.type === 'error' || matchInfo.status.includes('Hiba')) {
          displayText = matchInfo.status;
          statusType = 'error';
        } else if (isNewEntity) {
          displayText = matchInfo.status;
          statusType = 'new-entity';
        } else {
          displayText = matchInfo.status;
          statusType = 'error';
        }
        
        return {
          isMatched: isSuccessfulMatch,
          matchInfo: displayText,
          statusType: statusType
        };
      }
    }
    return null;
  }

  getFieldClass(column: string): string {
    // Add specific classes for certain column types
    const lowerColumn = column.toLowerCase();
    
    if (lowerColumn.includes('email') || lowerColumn.includes('e-mail')) {
      return 'field-email';
    }
    if (lowerColumn.includes('telefon') || lowerColumn.includes('phone')) {
      return 'field-phone';
    }
    if (lowerColumn.includes('név') || lowerColumn.includes('name')) {
      return 'field-name';
    }
    if (lowerColumn.includes('cím') || lowerColumn.includes('address')) {
      return 'field-address';
    }
    if (lowerColumn.includes('dátum') || lowerColumn.includes('date')) {
      return 'field-date';
    }
    
    return 'field-default';
  }

  isImportantField(column: string): boolean {
    const lowerColumn = column.toLowerCase();
    const importantFields = ['név', 'name', 'email', 'telefon', 'phone', 'szerződés', 'contract'];
    return importantFields.some(field => lowerColumn.includes(field));
  }

  onShowAllRows() {
    this.showPreview = false;
    this.preparePreviewData();
  }

  onShowPreview() {
    this.showPreview = true;
    this.preparePreviewData();
  }

  // Contract dropdown methods
  isContractField(column: string): boolean {
    const lowerColumn = column.toLowerCase();
    return lowerColumn.includes('szerződés szám') || 
           lowerColumn.includes('kock. vis. kezdete') || 
           lowerColumn.includes('díjjal fedezve') ||
           lowerColumn.includes('szerződés státusza') ||
           lowerColumn.includes('dijfiz. gyak') ||
           lowerColumn.includes('díjfizetési gyakoriság') ||
           lowerColumn.includes('szerződés díja') ||
           lowerColumn.includes('évesített díj') ||
           lowerColumn.includes('pénz nem') ||
           lowerColumn.includes('pénznem') ||
           lowerColumn.includes('lejárat') ||
           lowerColumn.includes('megszűnés dátuma') ||
           lowerColumn.includes('megszűnés');
  }

  getContractSelectionKey(rowIndex: number, column: string): string {
    return `${rowIndex}-${column}`;
  }

  onFieldMappingSelected(rowIndex: number, column: string, fieldName: string): void {
    const key = this.getContractSelectionKey(rowIndex, column);
    this.fieldMappings[key] = fieldName;
  }

  getSelectedField(rowIndex: number, column: string): string | null {
    const key = this.getContractSelectionKey(rowIndex, column);
    return this.fieldMappings[key] || null;
  }

  // Dialog methods
  onCloseDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    } else {
      this.closeDialog.emit();
    }
  }

  async onProcessData(): Promise<void> {
    if (!this.excelData || this.isProcessing) {
      return;
    }

    // Ellenőrizzük, hogy van-e legalább egy mező hozzárendelése
    const hasMappings = Object.values(this.fieldMappings).some(value => value !== null);
    if (!hasMappings) {
      alert('Kérem, válasszon ki legalább egy mezőt a contracts táblából!');
      return;
    }

    this.isProcessing = true;

    try {
      // Szűrjük ki a null értékeket
      const filteredMappings: { [key: string]: string } = {};
      for (const [key, value] of Object.entries(this.fieldMappings)) {
        if (value !== null) {
          filteredMappings[key] = value;
        }
      }

      const request: ProcessContractDataRequest = {
        filename: this.excelData.filename,
        rows_count: this.excelData.rows_count,
        columns: this.excelData.columns,
        data: this.excelData.data,
        fieldMappings: filteredMappings
      };

      console.log('Contract import kérés:', request);
      console.log('Field mappings részletesen:', JSON.stringify(filteredMappings, null, 2));
      console.log('Excel data struktúra első 2 sor:', JSON.stringify(this.excelData.data.slice(0, 2), null, 2));
      console.log('Excel columns:', this.excelData.columns);
      
      // Minden mapping key-value pár külön kiírása
      Object.entries(filteredMappings).forEach(([key, value]) => {
        console.log(`Excel oszlop "${key}" -> Contracts tábla mezője: "${value}"`);
      });

      const result = await this.contractsService.importContractData(request).toPromise();
      
      console.log('Contract import eredmény:', result);

      if (!result) {
        throw new Error('Nincs válasz a szervertől');
      }

      if (result.success) {
        // Ellenőrizzük, vannak-e hibás cégek
        if (result.failed_companies && result.failed_companies.length > 0) {
          // Részleges siker - vannak hibás cégek, de a többi sor sikeresen feltöltődött
          const dialogData: FailedCompaniesDialogData = {
            failed_companies: result.failed_companies,
            processed_count: result.processed_count,
            created_contracts: result.created_contracts,
            updated_contracts: result.updated_contracts
          };

          const failedCompaniesDialogRef = this.dialog.open(FailedCompaniesDialogComponent, {
            data: dialogData,
            width: '800px',
            maxWidth: '90vw',
            disableClose: true
          });

          const dialogResult = await failedCompaniesDialogRef.afterClosed().toPromise();
          
          if (dialogResult?.action === 'retry') {
            // Újrapróbálkozás a javított adatokkal
            console.log('Újrapróbálkozás a javított adatokkal:', dialogResult.companies_data);
            await this.retryWithCorrectedData(dialogResult.companies_data, request);
          }
        } else {
          // Nincs hibás cég, teljesen sikeres import
          const message = `Sikeres import!\n` +
            `Létrehozott szerződések: ${result.created_contracts}\n` +
            `Frissített szerződések: ${result.updated_contracts}\n` +
            `Feldolgozott sorok: ${result.processed_count}`;
          
          alert(message);
        }
        
        if (result.warnings.length > 0) {
          console.warn('Figyelmeztetések:', result.warnings);
        }
        
        // Bezárjuk a dialog-ot sikeres import után
        if (this.dialogRef) {
          this.dialogRef.close({
            action: 'success',
            result: result
          });
        } else {
          this.processData.emit(this.excelData);
        }
      } else {
        let errorMessage = `Import hiba: ${result.message}`;
        
        if (result.errors.length > 0) {
          errorMessage += '\n\nHibák:\n' + 
            result.errors.map(e => `Sor ${e.row}: ${e.error}`).join('\n');
        }
        
        alert(errorMessage);
      }

    } catch (error) {
      console.error('Contract import hiba:', error);
      alert('Hiba történt az import során. Kérem, próbálja újra.');
    } finally {
      this.isProcessing = false;
    }
  }

  async retryWithCorrectedData(correctedCompanies: any[], originalRequest: ProcessContractDataRequest): Promise<void> {
    try {
      this.isProcessing = true;

      const retryRequest = {
        failed_companies: correctedCompanies,
        original_request: originalRequest
      };

      console.log('🔄 === FRONTEND RETRY DEBUG ===');
      console.log('🔄 Corrected companies:', correctedCompanies);
      console.log('🔄 Original request filename:', originalRequest.filename);
      console.log('🔄 Original request rows_count:', originalRequest.rows_count);
      console.log('🔄 Original request columns:', originalRequest.columns);
      console.log('🔄 Original request data length:', originalRequest.data?.length);
      console.log('🔄 Original request first row:', originalRequest.data?.[0]);
      console.log('🔄 Original request fieldMappings count:', Object.keys(originalRequest.fieldMappings || {}).length);
      console.log('🔄 First 5 fieldMappings:', Object.fromEntries(Object.entries(originalRequest.fieldMappings || {}).slice(0, 5)));
      console.log('🔄 Complete retry request:', retryRequest);
      
      const result = await this.contractsService.retryFailedCompanies(retryRequest).toPromise();
      
      console.log('Újrapróbálkozás eredmény:', result);

      if (!result) {
        throw new Error('Nincs válasz a szervertől');
      }

      if (result.success) {
        let message = `Sikeres újrapróbálkozás!\n` +
          `Létrehozott szerződések: ${result.created_contracts}\n` +
          `Frissített szerződések: ${result.updated_contracts}\n` +
          `Feldolgozott sorok: ${result.processed_count}`;

        // Ellenőrizzük, vannak-e még mindig hibás cégek
        if (result.failed_companies && result.failed_companies.length > 0) {
          message += `\n\nFigyelem: ${result.failed_companies.length} cég továbbra sem került feltöltésre hiányos adatok miatt.`;
          
          // Újra megjelenítjük a dialog-ot a még mindig hibás cégekkel
          const dialogData: FailedCompaniesDialogData = {
            failed_companies: result.failed_companies,
            processed_count: result.processed_count,
            created_contracts: result.created_contracts,
            updated_contracts: result.updated_contracts
          };

          const failedCompaniesDialogRef = this.dialog.open(FailedCompaniesDialogComponent, {
            data: dialogData,
            width: '800px',
            maxWidth: '90vw',
            disableClose: true
          });

          const dialogResult = await failedCompaniesDialogRef.afterClosed().toPromise();
          
          if (dialogResult?.action === 'retry') {
            // Rekurzívan újrapróbálkozunk
            await this.retryWithCorrectedData(dialogResult.companies_data, originalRequest);
            return;
          }
        }

        // TODO: alert helyett snackbar használata, OK gombbal
        alert(message);
        
        // Bezárjuk a főablakot sikeres retry után
        if (this.dialogRef) {
          this.dialogRef.close({
            action: 'success',
            result: result
          });
        } else if (this.excelData) {
          this.processData.emit(this.excelData);
        }

      } else {
        let errorMessage = `Újrapróbálkozás hiba: ${result.message}`;
        
        if (result.errors && result.errors.length > 0) {
          errorMessage += '\n\nHibák:\n' + 
            result.errors.map(e => `Sor ${e.row}: ${e.error}`).join('\n');
        }
        
        alert(errorMessage);
      }

    } catch (error) {
      console.error('Újrapróbálkozás hiba:', error);
      alert('Hiba történt az újrapróbálkozás során. Kérem, próbálja újra.');
    } finally {
      this.isProcessing = false;
    }
  }
}